import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import './localOpsProofLanding.css';

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const getBackendOrigin = () =>
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  'http://localhost:4000';

const safeText = (value, fallback = '') => {
  const clean = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean || fallback;
};

const buildPostTaskPath = (regionSlug) => {
  const params = new URLSearchParams({
    intent: 'post_task',
    source: 'completion_proof_share',
  });
  if (regionSlug) params.set('regionSlug', regionSlug);
  return `/playermap?${params.toString()}`;
};

const normalizeMedia = (proof) => {
  const media = Array.isArray(proof?.safeMedia) ? proof.safeMedia : [];
  return media.find((item) => item?.url) || null;
};

const formatDuration = (value) => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const rounded = Math.round(totalSeconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const buildProofHeadline = (proof, regionLabel) => {
  const label = safeText(proof?.taskLabel, 'Local task completed');
  const completedLabel = /\bcompleted\b/i.test(label)
    ? label
    : `${label} completed`;
  return `${completedLabel} in ${regionLabel}`;
};

const buildVelocityLine = (proof) => {
  const acceptedDuration = formatDuration(
    proof?.acceptedInSeconds ?? proof?.velocity?.acceptedInSeconds,
  );
  const completedDuration = formatDuration(
    proof?.completedInSeconds ?? proof?.velocity?.completedInSeconds,
  );
  const parts = [];
  if (acceptedDuration) parts.push(`Accepted in ${acceptedDuration}`);
  if (completedDuration) parts.push(`Completed in ${completedDuration}`);
  return parts.length ? `${parts.join(' - ')} - Verified through JG LocalOps` : '';
};

export default function LocalOpsProofLanding() {
  const { proofId } = useParams();
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const prevBodyBg = document.body.style.background;
    const prevHtmlBg = document.documentElement.style.background;
    document.body.style.background = '#050912';
    document.documentElement.style.background = '#050912';
    return () => {
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevHtmlBg;
    };
  }, []);

  useEffect(() => {
    if (!proofId) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetch(`${getBackendOrigin()}/share/resolve/localops/proof/${proofId}`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => {
        if (!res.ok) throw new Error('not-found');
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setProof(json?.data || null);
        setNotFound(!json?.data);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [proofId]);

  const regionLabel = safeText(proof?.regionLabel, 'your area');
  const regionSlug = safeText(proof?.regionSlug, 'camden');
  const headline = buildProofHeadline(proof, regionLabel);
  const velocityLine = buildVelocityLine(proof);
  const helper = proof?.helper || null;
  const helperLine = helper?.handle
    ? `Completed by ${helper.handle}`
    : proof?.helperFallback || 'Completed by a local JG LocalOps helper';
  const postTaskPath = buildPostTaskPath(regionSlug);
  const heroMedia = useMemo(() => normalizeMedia(proof), [proof]);

  if (loading) {
    return (
      <main className="localops-proof-page">
        <section className="localops-proof-card localops-proof-card--loading">
          <JGLocalOpsMark className="localops-proof-mark" />
          <p>Loading completion proof...</p>
        </section>
      </main>
    );
  }

  if (notFound || !proof) {
    return (
      <main className="localops-proof-page">
        <section className="localops-proof-card">
          <JGLocalOpsMark className="localops-proof-mark" />
          <h1>Completion proof unavailable</h1>
          <p>
            This proof link may no longer be available, or the public proof is
            not share-safe.
          </p>
          <a className="localops-proof-cta" href={postTaskPath}>
            Post a Local Task
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-proof-page">
      <section className="localops-proof-card" data-testid="localops-proof-page">
        <div className="localops-proof-hero">
          {heroMedia ? (
            heroMedia.type === 'video' ? (
              <video
                className="localops-proof-media"
                src={heroMedia.url}
                controls
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                className="localops-proof-media"
                src={heroMedia.url}
                alt=""
              />
            )
          ) : (
            <div
              className="localops-proof-fallback"
              data-testid="localops-proof-fallback"
            >
              <JGLocalOpsMark className="localops-proof-mark" />
              <span>{proof.taskLabel || 'Local task completed'}</span>
              <strong>{headline}</strong>
              <p>Post the task. Watch it get done.</p>
            </div>
          )}
          {heroMedia ? <JGLocalOpsMark className="localops-proof-mark" /> : null}
        </div>

        <div className="localops-proof-body">
          <p className="localops-proof-kicker">Verified LocalOps completion</p>
          <h1>{headline}</h1>
          <div className="localops-proof-helper-chip">
            {helper?.profilePhoto ? (
              <img src={helper.profilePhoto} alt="" />
            ) : (
              <span className="localops-proof-helper-avatar">JG</span>
            )}
            <div>
              <strong>{helperLine}</strong>
              <span>Verified completion through JG LocalOps</span>
            </div>
          </div>
          <p>
            A local task was posted, accepted, and completed through JG
            LocalOps.
          </p>
          {velocityLine ? (
            <p className="localops-proof-velocity">{velocityLine}</p>
          ) : null}
          <p className="localops-proof-brand-line">
            Post the task. Watch it get done.
          </p>
          <div className="localops-proof-actions">
            <a className="localops-proof-cta" href={postTaskPath}>
              Post a Local Task
            </a>
            {helper?.profilePath ? (
              <a className="localops-proof-secondary" href={helper.profilePath}>
                View helper profile
              </a>
            ) : null}
          </div>
          <p className="localops-proof-footer">
            Local help. Real people. Action nearby.
          </p>
        </div>
      </section>
    </main>
  );
}
